import React, { useState } from 'react';
import { Book, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { bookInfo, chapters, ladrillos } from '../data/mockData';
import { Link } from 'react-router-dom';

const ElLibro = () => {
  const [selectedChapter, setSelectedChapter] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Book Cover */}
              <div className="relative">
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-2xl shadow-gold">
                  <img
                    src={bookInfo.coverImage}
                    alt="El Muro Endler - Portada"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Book Info */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-5xl md:text-6xl font-bold text-muro-text mb-4">
                    <span className="text-gold-gradient">EL MURO</span>
                    <br />
                    <span className="text-muro-text">ENDLER</span>
                  </h1>
                  <p className="text-xl text-muro-text-muted mb-6">
                    {bookInfo.subtitle}
                  </p>
                </div>

                <div className="bg-muro-gray-900 p-6 rounded-lg space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muro-text-muted">Autor:</span>
                    <span className="text-muro-text font-medium">{bookInfo.author}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muro-text-muted">Páginas:</span>
                    <span className="text-muro-text font-medium">{bookInfo.pages}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muro-text-muted">Lanzamiento:</span>
                    <span className="text-muro-text font-medium">Junio 2026</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muro-text-muted">Editorial:</span>
                    <span className="text-muro-text font-medium">{bookInfo.publisher}</span>
                  </div>
                  <div className="pt-4 border-t border-muro-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-muro-text-muted">Precio Preventa:</span>
                      <span className="text-3xl font-bold text-gold-gradient">
                        ${bookInfo.price.toLocaleString('es-CL')}
                      </span>
                    </div>
                  </div>
                </div>

                <Link to="/comprar">
                  <Button className="btn-gold w-full py-6 text-lg rounded-md">
                    Comprar Ahora en Preventa
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl font-bold text-muro-text gold-accent-line inline-block">
              Sobre el Libro
            </h2>
            <p className="text-lg text-muro-text-muted leading-relaxed">
              {bookInfo.description}
            </p>
            <div className="pt-6">
              <p className="text-xl text-muro-gold italic">
                "{bookInfo.tagline}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters Visualizer */}
      <section className="py-20 brick-texture" id="capitulos">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4">
                Los <span className="text-gold-gradient">8 Capítulos</span>
              </h2>
              <p className="text-muro-text-muted text-lg">
                Explora cada capítulo del muro. Haz clic para ver el resumen completo.
              </p>
            </div>

            <div className="space-y-4">
              {chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="bg-muro-gray-900 border border-muro-gray-800 rounded-lg overflow-hidden transition-all duration-300 hover:border-muro-gold"
                >
                  <button
                    onClick={() => setSelectedChapter(selectedChapter === chapter.id ? null : chapter.id)}
                    className="w-full p-6 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="bg-muro-gold/10 p-3 rounded-lg">
                        <Book className="w-6 h-6 text-muro-gold" />
                      </div>
                      <div className="flex-1">
                        <div className="text-muro-gold text-sm font-semibold mb-1">
                          {chapter.number}
                        </div>
                        <h3 className="text-xl font-bold text-muro-text mb-1">
                          {chapter.title}
                        </h3>
                        <p className="text-muro-text-muted text-sm">
                          {chapter.subtitle}
                        </p>
                      </div>
                    </div>
                    {selectedChapter === chapter.id ? (
                      <ChevronUp className="w-5 h-5 text-muro-gold" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muro-text-muted" />
                    )}
                  </button>

                  {selectedChapter === chapter.id && (
                    <div className="px-6 pb-6 space-y-4 border-t border-muro-gray-800 pt-6">
                      <div>
                        <h4 className="text-muro-gold font-semibold mb-2">Resumen</h4>
                        <p className="text-muro-text-muted leading-relaxed">
                          {chapter.summary}
                        </p>
                      </div>
                      <div className="bg-muro-gold/5 p-4 rounded-lg border-l-4 border-muro-gold">
                        <h4 className="text-muro-gold font-semibold mb-2">Lección del Capítulo</h4>
                        <p className="text-muro-text italic">
                          {chapter.lesson}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muro-text-muted">
                        <span className="flex items-center">
                          📅 {chapter.year}
                        </span>
                        <span className="flex items-center">
                          📍 {chapter.location}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Los 10 Ladrillos */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4">
                Los <span className="text-gold-gradient">10 Ladrillos</span> del Muro
              </h2>
              <p className="text-muro-text-muted text-lg max-w-2xl mx-auto">
                Los principios que construyeron a la mejor arquera del mundo. Y que pueden construir tu propio muro.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {ladrillos.map((ladrillo) => (
                <div
                  key={ladrillo.id}
                  className="glass-morphism p-6 rounded-lg card-hover"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-muro-gold text-muro-black w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                      {ladrillo.id}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-muro-text mb-1">
                        {ladrillo.title}
                      </h3>
                      <p className="text-muro-gold text-sm mb-3">
                        {ladrillo.subtitle}
                      </p>
                      <p className="text-muro-text-muted text-sm leading-relaxed">
                        {ladrillo.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-muro-text">
              ¿Listo para construir tu muro?
            </h2>
            <p className="text-xl text-muro-text-muted">
              Consigue tu ejemplar en preventa y empieza a levantar tu defensa inquebrantable.
            </p>
            <Link to="/comprar">
              <Button className="btn-gold px-10 py-6 text-xl rounded-md">
                Comprar en Preventa
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ElLibro;
